import { useNavigate } from 'react-router-dom';
import { ArrowRight, MessageCircle, Sparkles } from 'lucide-react';
import { joinWaitlist } from '../lib/waitlist';

/* "I Want Access" — shown after the assessment results.
   Purpose: capture genuine interest in wellness experts BEFORE the network launches.
   No expert names, no expert cards, no fake profiles. */

const WA_NUMBER = '918308595553';
const WA_MSG = "Hi Clinity, I'd like access to wellness experts.";
const WA_URL = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(WA_MSG)}`;

const DIMENSION_LABELS = [
  'Physical', 'Mental', 'Emotional', 'Occupational',
  'Financial', 'Lifestyle & Environmental', 'Spiritual', 'Self-Care',
];

export default function RecommendedExperts({ assessmentId }) {
  const navigate = useNavigate();

  const handleAccess = () => {
    joinWaitlist({ source: 'result_want_access', assessment_id: assessmentId });
  };

  return (
    <section className="mt-12" data-testid="want-access-section">
      <div
        className="relative rounded-[28px] overflow-hidden p-8 sm:p-12 md:p-14"
        style={{
          background: 'linear-gradient(135deg, #F5F3FF 0%, #EEF2FF 60%, #FFFFFF 100%)',
          border: '1px solid #E0E7FF',
          boxShadow: '0 24px 60px -30px rgba(79,70,229,0.25)',
        }}
      >
        {/* Soft indigo halo */}
        <div
          aria-hidden="true"
          className="absolute -top-16 -right-16 w-64 h-64 rounded-full blur-3xl opacity-40 pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.28) 0%, transparent 70%)' }}
        />

        <div className="relative text-center max-w-2xl mx-auto">
          <div
            className="inline-flex items-center gap-2 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.28em]"
            style={{ color: '#4F46E5' }}
            data-testid="want-access-eyebrow"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Grow with expert guidance
          </div>

          <h2
            className="mt-5 text-[28px] sm:text-[36px] lg:text-[44px] font-bold leading-[1.1]"
            style={{
              fontFamily: 'Outfit, sans-serif',
              color: '#0F172A',
              letterSpacing: '-0.028em',
            }}
            data-testid="want-access-headline"
          >
            Your wellbeing journey doesn&apos;t have to be solo.
          </h2>

          <p
            className="mt-5 text-[15px] sm:text-[17px] leading-[1.65] max-w-xl mx-auto"
            style={{ color: '#475569', fontFamily: 'Manrope, sans-serif' }}
            data-testid="want-access-subheadline"
          >
            Connect with wellness experts across 8 dimensions to improve your overall wellbeing.
          </p>

          {/* 8 dimension chips — visual reinforcement, NO names */}
          <div className="mt-7 flex flex-wrap items-center justify-center gap-2" data-testid="want-access-dims">
            {DIMENSION_LABELS.map((d) => (
              <span
                key={d}
                className="px-3 py-1.5 rounded-full text-[12px] font-semibold bg-white"
                style={{ color: '#4F46E5', border: '1px solid #E0E7FF' }}
              >
                {d}
              </span>
            ))}
          </div>

          <div className="mt-10">
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleAccess}
              data-testid="i-want-access-btn"
              className="group inline-flex items-center justify-center gap-3 px-9 py-4 sm:px-11 sm:py-[18px] rounded-full font-semibold text-[15px] sm:text-base text-white no-underline transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0"
              style={{
                background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #A855F7 100%)',
                boxShadow: '0 20px 40px -12px rgba(139,92,246,0.55), inset 0 1px 0 rgba(255,255,255,0.18)',
                letterSpacing: '-0.005em',
              }}
            >
              <MessageCircle className="w-[18px] h-[18px]" strokeWidth={2.2} />
              I Want Access
              <ArrowRight className="w-[18px] h-[18px] transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2.2} />
            </a>
            <p className="mt-3 text-xs" style={{ color: '#94A3B8' }}>
              We&apos;ll reach out on WhatsApp with next steps.
            </p>
          </div>

          <div className="mt-8">
            <button
              onClick={() => navigate('/experts')}
              className="text-[13px] font-semibold inline-flex items-center gap-1.5 transition-transform hover:translate-x-0.5"
              style={{ color: '#6366F1' }}
              data-testid="want-access-learn-more"
            >
              Explore the 8 dimensions <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
